import {
  DEVICE_SIZES,
  IMAGE_SIZES,
} from '@package/configurations/nextjs/images';

const ALLOWED_WIDTHS = [...IMAGE_SIZES, ...DEVICE_SIZES];
const MAX_WIDTH = ALLOWED_WIDTHS[ALLOWED_WIDTHS.length - 1];

/** Rounds up to the next width the image resizer accepts, capped at the largest */
const getAllowedWidth = (width: number) =>
  ALLOWED_WIDTHS.find(size => size >= width) ?? MAX_WIDTH;

const aspectRatios: Record<Content.Properties.AspectRatio, number> = {
  fourByThree: 3 / 4,
  nineBySixteen: 16 / 9,
  oneByOne: 1,
  sixteenByNine: 9 / 16,
  threeByFour: 4 / 3,
  threeByOne: 1 / 3,
};

/** Oversizes single-source (unoptimized) images to cover high density screens */
const SIZE_MULTIPLIER = 1.5;

export const formatImageUrl = (
  url: string,
  parameters: {
    aspectRatio?: Content.Properties.AspectRatio | null;
    focalPoint?: FocalPoint | null;
    format?: 'webp';
    height?: number;
    quality?: number;
    /** Set to 1 when the browser picks the density itself (e.g. next/image srcset) */
    sizeMultiplier?: number;
    width: number;
  },
) => {
  const sizeMultiplier = parameters.sizeMultiplier ?? SIZE_MULTIPLIER;

  if (!url) return '';

  if (url.startsWith('/images/') || url.includes('umbraco')) {
    return url;
  }

  let formattedUrl = `${url}`;

  const requestedWidth = Math.ceil(parameters.width * sizeMultiplier);
  const width = getAllowedWidth(requestedWidth);
  // The height follows the rounding of the width, so the crop keeps its aspect ratio
  const scale = width / requestedWidth;

  if (parameters.width) {
    const hasQuery = formattedUrl.includes('?');

    formattedUrl += `${hasQuery ? '&' : '?'}width=${width}`;
  }

  if (parameters.height) {
    const hasQuery = formattedUrl.includes('?');

    formattedUrl += `${hasQuery ? '&' : '?'}height=${Math.round(Math.ceil(parameters.height * sizeMultiplier) * scale)}`;
  } else if (parameters.aspectRatio) {
    const hasQuery = formattedUrl.includes('?');

    formattedUrl += `${hasQuery ? '&' : '?'}height=${Math.round(width * aspectRatios[parameters.aspectRatio])}`;
  }

  if (parameters.focalPoint) {
    const hasQuery = formattedUrl.includes('?');

    const left = parameters.focalPoint?.leftPercentage ?? 50;
    const top = parameters.focalPoint?.topPercentage ?? 50;
    const anchorPoint = `${(left / 100).toFixed(2)},${(top / 100).toFixed(2)}`;

    formattedUrl += `${hasQuery ? '&' : '?'}rxy=${anchorPoint}`;
  }

  if (parameters.format) {
    const hasQuery = formattedUrl.includes('?');

    formattedUrl += `${hasQuery ? '&' : '?'}format=${parameters.format}`;
  }

  if (parameters.quality) {
    const hasQuery = formattedUrl.includes('?');

    formattedUrl += `${hasQuery ? '&' : '?'}quality=${parameters.quality}`;
  }

  return formattedUrl;
};
